import React, { useEffect, useState } from "react";
import AdminModal from "components/admin/AdminModal";
import FormField from "components/admin/FormField";
import {
  addOgUserGroup,
  getApiErrorMessage,
  OgUserGroupPayload,
  updateOgUserGroup,
} from "apis/adminServices/userManagmentService";
import { useToastStore } from "store/toastStore";
import { OgUserGroup } from "./type";

interface OgUserGroupFormModalProps {
  isOpen: boolean;
  /** The group being edited, or null to create a new one. */
  group: OgUserGroup | null;
  onClose: () => void;
  onSaved: () => void;
}

const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 150;
const USER_COUNT_MAX_DIGITS = 5;
const USER_COUNT_MAX_VALUE = 99999;
const FREE_TOKEN_MAX_DIGITS = 4;
const FREE_TOKEN_MAX_VALUE = 9999;

const inputClass =
  "w-full px-3 py-2.5 rounded-md border border-gray-300 text-sm text-grease-100 focus:outline-none focus:border-royalblue-100 disabled:bg-gray-50 disabled:text-lightgray-200";

/**
 * Add / edit form for an OG user group, opened from the Manage Users Roles
 * modal. On edit only the fields the admin actually touched go up — the update
 * endpoint keeps anything the body omits.
 */
const OgUserGroupFormModal: React.FC<OgUserGroupFormModalProps> = ({
  isOpen,
  group,
  onClose,
  onSaved,
}) => {
  const isEdit = group !== null;
  const [roleName, setRoleName] = useState("");
  const [description, setDescription] = useState("");
  const [userCount, setUserCount] = useState("");
  const [freeTokenAmount, setFreeTokenAmount] = useState("");
  const [isUnlimited, setIsUnlimited] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const { showToast } = useToastStore();

  // Reopening the form always starts from what the server last told us.
  useEffect(() => {
    if (!isOpen) return;
    setRoleName(group?.roleName ?? "");
    setDescription(group?.description ?? "");
    setUserCount(group?.userCount != null ? String(group.userCount) : "");
    setFreeTokenAmount(
      group?.freeTokenAmount != null ? String(group.freeTokenAmount) : ""
    );
    setIsUnlimited(group ? group.freeTokenAmount == null : true);
    setErrors({});
    setSaving(false);
  }, [isOpen, group]);

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};

    const trimmedName = roleName.trim();
    if (!trimmedName) {
      nextErrors.roleName = "Role name is required.";
    } else if (trimmedName.length < 2) {
      nextErrors.roleName = "Role name must be at least 2 characters.";
    } else if (trimmedName.length > NAME_MAX_LENGTH) {
      nextErrors.roleName = `Role name cannot exceed ${NAME_MAX_LENGTH} characters.`;
    }

    if (description.length > DESCRIPTION_MAX_LENGTH) {
      nextErrors.description = `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters.`;
    }

    const cleanCount = userCount.replace(/\D/g, "").trim();
    if (!cleanCount) {
      nextErrors.userCount = "Enter a valid user count.";
    } else {
      const count = parseInt(cleanCount, 10);
      if (isNaN(count) || count < 1) {
        nextErrors.userCount = "User count must be a whole number of 1 or more.";
      } else if (count > USER_COUNT_MAX_VALUE) {
        nextErrors.userCount = `User count cannot exceed 5 digits (max ${USER_COUNT_MAX_VALUE.toLocaleString()}).`;
      }
    }

    if (!isUnlimited) {
      const cleanTokens = freeTokenAmount.replace(/\D/g, "").trim();
      if (!cleanTokens) {
        nextErrors.freeTokenAmount = "Enter a valid free token amount.";
      } else {
        const amount = parseInt(cleanTokens, 10);
        if (isNaN(amount) || amount < 1) {
          nextErrors.freeTokenAmount = "Free tokens must be a whole number of 1 or more.";
        } else if (amount > FREE_TOKEN_MAX_VALUE) {
          nextErrors.freeTokenAmount = `Free tokens cannot exceed 4 digits (max ${FREE_TOKEN_MAX_VALUE.toLocaleString()}).`;
        }
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNumericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "Tab" ||
      e.key === "Escape" ||
      e.key === "Enter" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.ctrlKey ||
      e.metaKey
    ) {
      return;
    }
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * On edit, diff against the loaded group so untouched fields stay out of the
   * body. Switching a fixed amount back to Unlimited needs the dedicated flag —
   * sending `freeTokenAmount: null` would just be read as "keep the current
   * value".
   */
  const buildPayload = (): OgUserGroupPayload | null => {
    const name = roleName.trim();
    const cleanTokens = freeTokenAmount.replace(/\D/g, "").trim();
    const amount =
      isUnlimited || !cleanTokens
        ? null
        : parseInt(cleanTokens, 10);
    const cleanCount = userCount.replace(/\D/g, "").trim();
    const count = cleanCount ? parseInt(cleanCount, 10) : null;

    if (!group) {
      const payload: OgUserGroupPayload = { roleName: name };
      if (description.trim()) payload.description = description.trim();
      if (count !== null && count >= 1) payload.userCount = count;
      if (amount !== null && amount >= 1) payload.freeTokenAmount = amount;
      return payload;
    }

    const payload: OgUserGroupPayload = {};
    if (name !== group.roleName) payload.roleName = name;
    if (description.trim() !== (group.description ?? "").trim()) {
      payload.description = description.trim();
    }
    if (count !== null && count >= 1 && count !== group.userCount) {
      payload.userCount = count;
    }
    if (amount === null) {
      if (group.freeTokenAmount !== null) {
        payload.setFreeTokenAmountUnlimited = true;
      }
    } else if (amount !== group.freeTokenAmount) {
      payload.freeTokenAmount = amount;
    }

    return Object.keys(payload).length > 0 ? payload : null;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = buildPayload();
    if (!payload) {
      onClose();
      return;
    }

    try {
      setSaving(true);
      const response = group
        ? await updateOgUserGroup(group.id, payload)
        : await addOgUserGroup(payload);

      showToast(
        isEdit
          ? "User role updated successfully."
          : "User role created successfully.",
        "success"
      );
      onSaved();
    } catch (error) {
      showToast(
        getApiErrorMessage(
          error,
          isEdit
            ? "Failed to update the user role."
            : "Failed to create the user role."
        ),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={() => {
        if (!saving) onClose();
      }}
      title={isEdit ? "Edit User Role" : "Add New User Role"}
      size="lg"
      showCloseButton={!saving}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-6 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="create-button px-6 py-2 text-sm rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (isEdit ? "Updating..." : "Saving...") : isEdit ? "Update" : "Create"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label="Role Name" htmlFor="og-role-name" required error={errors.roleName}>
          <input
            id="og-role-name"
            autoFocus
            className={inputClass}
            placeholder="Enter role name"
            maxLength={NAME_MAX_LENGTH}
            value={roleName}
            disabled={saving}
            onChange={(e) => {
              const clean = e.target.value.slice(0, NAME_MAX_LENGTH);
              setRoleName(clean);
              setErrors((prev) => ({ ...prev, roleName: "" }));
            }}
          />
        </FormField>

        <FormField
          label="Description"
          htmlFor="og-role-description"
          error={errors.description}
        >
          <input
            id="og-role-description"
            className={inputClass}
            placeholder="Describe what this group gets"
            maxLength={DESCRIPTION_MAX_LENGTH}
            value={description}
            disabled={saving}
            onChange={(e) => {
              const clean = e.target.value.slice(0, DESCRIPTION_MAX_LENGTH);
              setDescription(clean);
              setErrors((prev) => ({ ...prev, description: "" }));
            }}
            onPaste={(e) => {
              e.preventDefault();
              const text = e.clipboardData.getData("text");
              const clean = text.slice(0, DESCRIPTION_MAX_LENGTH);
              setDescription(clean);
              setErrors((prev) => ({ ...prev, description: "" }));
            }}
          />
          <div className="flex justify-end mt-1">
            <span className="text-[11px] text-gray-400">
              {description.length}/{DESCRIPTION_MAX_LENGTH}
            </span>
          </div>
        </FormField>

        <FormField label="User Count" htmlFor="og-role-user-count" required error={errors.userCount}>
          <input
            id="og-role-user-count"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className={inputClass}
            placeholder="Number of users allowed in this group (max 5 digits)"
            value={userCount}
            disabled={saving}
            onKeyDown={handleNumericKeyDown}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, USER_COUNT_MAX_DIGITS);
              setUserCount(digitsOnly);
              setErrors((prev) => ({ ...prev, userCount: "" }));
            }}
            onPaste={(e) => {
              e.preventDefault();
              const text = e.clipboardData.getData("text");
              const digitsOnly = text.replace(/\D/g, "").slice(0, USER_COUNT_MAX_DIGITS);
              setUserCount(digitsOnly);
              setErrors((prev) => ({ ...prev, userCount: "" }));
            }}
          />
        </FormField>

        <FormField label="Free Tokens" htmlFor="og-role-free-token">
          <input
            id="og-role-free-token"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className={inputClass}
            placeholder="Tokens credited on joining this group (max 4 digits)"
            value={isUnlimited ? "" : freeTokenAmount}
            disabled={saving || isUnlimited}
            onKeyDown={handleNumericKeyDown}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, FREE_TOKEN_MAX_DIGITS);
              setFreeTokenAmount(digitsOnly);
              setErrors((prev) => ({ ...prev, freeTokenAmount: "" }));
            }}
            onPaste={(e) => {
              e.preventDefault();
              const text = e.clipboardData.getData("text");
              const digitsOnly = text.replace(/\D/g, "").slice(0, FREE_TOKEN_MAX_DIGITS);
              setFreeTokenAmount(digitsOnly);
              setErrors((prev) => ({ ...prev, freeTokenAmount: "" }));
            }}
          />
          {errors.freeTokenAmount && (
            <p className="text-xs text-red-500 mt-1.5">{errors.freeTokenAmount}</p>
          )}
          <label className="flex items-center gap-2 mt-3 text-sm text-grease-100 cursor-pointer w-fit">
            <input
              type="checkbox"
              className="accent-primaryblue-100 cursor-pointer"
              checked={isUnlimited}
              disabled={saving}
              onChange={(e) => {
                setIsUnlimited(e.target.checked);
                setErrors((prev) => ({ ...prev, freeTokenAmount: "" }));
              }}
            />
            Set as Unlimited
          </label>
        </FormField>
      </div>
    </AdminModal>
  );
};

export default OgUserGroupFormModal;
